import React from 'react';
import { Link, LinkProps, SxProps, Theme, useTheme } from '@mui/material';

export interface DtLinkProps extends Omit<LinkProps, 'sx' | 'variant'> {
    children: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    colorVariant?: 'primary' | 'secondary' | 'error' | 'success';
    size?: 'small' | 'medium' | 'large';
    sx?: SxProps<Theme>;
}

const DtLink: React.FC<DtLinkProps> = ({
    children,
    onClick,
    disabled = false,
    colorVariant = 'primary',
    size = 'medium',
    sx,
    ...props
}) => {
    const theme = useTheme();

    const getVariantStyles = () => {
        const baseStyles = {
            cursor: disabled ? 'not-allowed' : 'pointer',
            textDecoration: 'underline',
            textDecorationColor: 'transparent',
            fontWeight: theme.typography.fontWeightMedium,
            border: 'none',
            background: 'none',
            padding: 0,
            transition: theme.transitions.create([
                'color',
                'text-decoration-color',
                'opacity'
            ], {
                duration: theme.transitions.duration.short,
            }),
            '&:hover': disabled ? {} : {
                textDecorationColor: 'currentColor',
                opacity: 0.8,
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: '2px',
                borderRadius: theme.spacing(0.5),
            },
        };

        const getColorFromVariant = (variant: string) => {
            switch (variant) {
                case 'primary':
                    return theme.palette.primary.main;
                case 'secondary':
                    return theme.palette.secondary.main;
                case 'error':
                    return theme.palette.error.main;
                case 'success':
                    return theme.palette.success.main;
                default:
                    return theme.palette.primary.main;
            }
        };

        const variantStyles = {
            color: disabled ? theme.palette.text.disabled : getColorFromVariant(colorVariant),
        };

        const sizeStyles = {
            small: {
                fontSize: theme.typography.body2.fontSize,
                lineHeight: theme.typography.body2.lineHeight,
            },
            medium: {
                fontSize: theme.typography.body1.fontSize,
                lineHeight: theme.typography.body1.lineHeight,
            },
            large: {
                fontSize: theme.typography.h6.fontSize,
                lineHeight: theme.typography.h6.lineHeight,
            }
        };

        return {
            ...baseStyles,
            ...variantStyles,
            ...sizeStyles[size]
        };
    };

    const handleClick = (event: React.MouseEvent) => {
        if (disabled) {
            event.preventDefault();
            return;
        }
        if (onClick) {
            onClick();
        }
    };

    return (
        <Link
            component="button"
            variant="body2"
            onClick={handleClick}
            sx={{
                ...getVariantStyles(),
                ...sx
            }}
            disabled={disabled}
            {...props}
        >
            {children}
        </Link>
    );
};

export default DtLink;